import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Rss, Play, Heart, Volume2, VolumeX } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const LiveFeed = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [mutedVideos, setMutedVideos] = useState({});

  useEffect(() => {
    fetchPosts();
    // Refresh every 30 seconds
    const interval = setInterval(fetchPosts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/feed/posts?limit=10`);
      setPosts(response.data.slice(0, 5)); // Show latest 5
    } catch (error) {
      console.log('Feed not available');
      // Use demo data if API fails
      setPosts([
        { id: '1', user: { username: 'PartyPete' }, bar_name: 'The Blue Lounge', content: '🔥 This place is packed tonight!', created_at: new Date().toISOString(), likes: 12 },
        { id: '2', user: { username: 'NightOwl' }, bar_name: 'Neon Nights', content: 'Great DJ spinning! 🎵', created_at: new Date(Date.now() - 300000).toISOString(), likes: 8 },
        { id: '3', user: { username: 'CocktailQueen' }, bar_name: 'Rooftop Bar', content: 'Happy hour specials are amazing 🍸', created_at: new Date(Date.now() - 600000).toISOString(), likes: 15 },
        { id: '4', user: { username: 'BarHopper' }, bar_name: 'The Irish Pub', content: 'Live music tonight! 🎸', created_at: new Date(Date.now() - 900000).toISOString(), likes: 22 },
        { id: '5', user: { username: 'MixMaster' }, bar_name: 'Velvet Underground', content: 'Best cocktails in town 🍹', created_at: new Date(Date.now() - 1200000).toISOString(), likes: 18 },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const toggleMute = (postId) => {
    setMutedVideos(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const handleVideoClick = (postId) => {
    setPlayingVideo(playingVideo === postId ? null : postId);
  };

  if (loading) {
    return (
      <Card className="glass p-4 border border-slate-700 mb-4">
        <div className="flex items-center gap-2 text-purple-400">
          <Rss className="w-4 h-4 animate-pulse" />
          <span className="text-sm">Loading live feed...</span>
        </div>
      </Card>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <Card className="glass p-4 border border-purple-500/30 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="relative">
          <Rss className="w-5 h-5 text-purple-400" />
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
        </div>
        <h3 className="font-semibold text-white text-sm">Live Feed</h3>
        <span className="text-xs text-slate-400">from all bars</span>
      </div>
      
      {/* Vertical scrollable feed */}
      <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
        {posts.map((post) => (
          <div
            key={post.id}
            className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-purple-500/50 transition-all"
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                {post.user?.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium text-white">{post.user?.username || 'Anonymous'}</p>
                  <span className="text-xs text-purple-400">@ {post.bar_name || 'Unknown Bar'}</span>
                </div>
                
                {/* Video Content */}
                {post.video_url ? (
                  <div className="relative rounded-lg overflow-hidden mb-2 max-w-[280px]">
                    <video
                      src={post.video_url}
                      className="w-full aspect-video object-cover bg-slate-900 rounded-lg"
                      controls={playingVideo === post.id}
                      muted={mutedVideos[post.id] !== false}
                      playsInline
                      onClick={() => handleVideoClick(post.id)}
                      onPlay={() => setPlayingVideo(post.id)}
                    />
                    {playingVideo !== post.id && (
                      <div 
                        className="absolute inset-0 flex items-center justify-center bg-black/40 cursor-pointer hover:bg-black/30 transition-all"
                        onClick={() => handleVideoClick(post.id)}
                      >
                        <div className="w-12 h-12 rounded-full bg-purple-600/80 flex items-center justify-center">
                          <Play className="w-6 h-6 text-white ml-1" fill="white" />
                        </div>
                      </div>
                    )}
                    {playingVideo === post.id && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleMute(post.id); }}
                        className="absolute bottom-2 right-2 p-1.5 bg-black/60 rounded-full hover:bg-black/80 transition-all"
                      >
                        {mutedVideos[post.id] === false ? (
                          <Volume2 className="w-4 h-4 text-white" />
                        ) : (
                          <VolumeX className="w-4 h-4 text-white" />
                        )}
                      </button>
                    )}
                  </div>
                ) : (
                  /* Text Content */
                  <p className="text-sm text-slate-300 mb-2">{post.content || post.caption}</p>
                )}
                
                <div className="flex items-center gap-4 text-xs text-slate-400">
                  <span>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</span>
                  <span className="flex items-center gap-1">
                    <Heart className="w-3 h-3" /> {post.likes || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default LiveFeed;
